/**
 * Meeting Posts Page
 * /meetings/:id/posts - 모임 게시글 목록 페이지
 */

import { boardCategories } from "@/components/meetings/MeetingSidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName } from "@/utils/displayName";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { ArrowLeft, MessageCircle, Plus } from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";

interface MeetingPost {
  id: string;
  title?: string;
  content: string;
  images: string[];
  authorNickname: string;
  authorLocation?: string;
  category: string;
  createdAt: string;
  likes: number;
  comments: number;
}

interface Meeting {
  id: string;
  title: string;
  posts: MeetingPost[];
  stats: {
    totalPosts: number;
  };
}

export default function MeetingPostsPage() {
  const [match, params] = useRoute<{ id: string }>("/meetings/:id/posts");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("all");

  const meetingId = params?.id;

  // 모임 기본 정보 조회
  const { data: meetingData } = useQuery<{
    ok: boolean;
    data: { id: string; title: string; stats?: { totalPosts: number } };
  }>({
    queryKey: ["/api/meetings", meetingId],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}`);
      if (!response.ok) throw new Error("Failed to fetch meeting");
      return response.json();
    },
    enabled: !!meetingId,
  });

  // 게시글 목록 조회 (전용 API)
  const { data: postsData, isLoading } = useQuery<{
    ok: boolean;
    data: {
      meetingId: string;
      total: number;
      posts: MeetingPost[];
    };
  }>({
    queryKey: ["/api/meetings", meetingId, "posts", selectedCategory],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const url = `/api/meetings/${meetingId}/posts?category=${selectedCategory}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch posts");
      return response.json();
    },
    enabled: !!meetingId,
  });

  const meeting = meetingData?.ok ? meetingData.data : null;
  const rawPosts = postsData?.ok ? postsData.data?.posts || [] : [];
  const actualPostCount = postsData?.ok
    ? postsData.data?.total || rawPosts.length
    : 0;
  const filteredPosts = rawPosts.filter(
    (post): post is (typeof rawPosts)[number] & Record<string, any> =>
      Boolean(post) && typeof post === "object"
  );

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              유효하지 않은 모임 ID입니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">로딩 중...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              모임 정보를 찾을 수 없습니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/meetings/${meetingId}`)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold">
              {meeting.title} - 게시글 ({actualPostCount}개)
            </h1>
          </div>
          {user && (
            <Button
              size="sm"
              onClick={() => navigate(`/meetings/${meetingId}/posts/new`)}
            >
              <Plus className="w-4 h-4 mr-2" />
              글쓰기
            </Button>
          )}
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* 카테고리 필터 */}
        <div className="mb-6">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {boardCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  variant={
                    selectedCategory === cat.id ? "default" : "secondary"
                  }
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {"emoji" in cat && cat.emoji && (
                    <span className="mr-1">{cat.emoji}</span>
                  )}
                  {cat.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* 게시글 리스트 */}
        {filteredPosts.length > 0 ? (
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <Card
                key={post.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() =>
                  navigate(`/meetings/${meetingId}/posts/${post.id}`)
                }
              >
                <CardContent className="p-6">
                  {post.title && (
                    <h3 className="text-lg font-semibold mb-2">{post.title}</h3>
                  )}
                  <p className="text-sm mb-4 line-clamp-3">{post.content}</p>

                  {post.images && post.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {post.images.slice(0, 3).map((image, index) => (
                        <div
                          key={index}
                          className="aspect-square rounded-lg overflow-hidden bg-muted"
                        >
                          <img
                            src={image}
                            alt={`${post.title || "게시글"} 이미지 ${
                              index + 1
                            }`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>
                      {getDisplayName(post)}
                      {post.authorLocation && `(${post.authorLocation})`}
                    </span>
                    <span>·</span>
                    <span>
                      {post.createdAt
                        ? formatDistanceToNow(new Date(post.createdAt), {
                            addSuffix: true,
                            locale: ko,
                          })
                        : "방금 전"}
                    </span>
                    <span>·</span>
                    <span>
                      {
                        boardCategories.find((c) => c.id === post.category)
                          ?.label
                      }
                    </span>
                    {post.likes > 0 && (
                      <>
                        <span>·</span>
                        <span>👍 {post.likes}</span>
                      </>
                    )}
                    {post.comments > 0 && (
                      <>
                        <span>·</span>
                        <span>💬 {post.comments}</span>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                {selectedCategory === "all"
                  ? "아직 게시글이 없습니다"
                  : `${
                      boardCategories.find((c) => c.id === selectedCategory)
                        ?.label || "이 카테고리"
                    }에 게시글이 없습니다`}
              </p>
              {user && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/meetings/${meetingId}/posts/new`)}
                >
                  첫 게시글 작성하기
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
